import axios from 'axios';
import { useState } from 'react';
import { useIntl } from 'react-intl';
import { useToasts } from 'react-toast-notifications';
import qs from 'qs';
import { useQuery } from 'react-query';
import { makeApiUrl, makeMutationFn } from '../../utils/reactQuery/index';
import { utcToLocal } from '../../utils/date';

import { FailedOrder } from '../../assets/svg/icons';
import apiRoutes from '../../setup/consts/apiRoutes';
import { OrdersGridFilters, UsePlaceholderPropsReturnType } from './types';
import { downloadFile } from '../../utils';
import { formatDateDashes } from '../../components/atoms/Fields/Datepicker/utils';

export type ReasonBucket = {
  reason: string;
  count: number;
  pct: number;
  trend: 'up' | 'down' | 'stable';
  priorCount: number;
};

type ApiReasonBucket = {
  reason: string;
  count: number;
  percentage: number;
};

type BreakdownApiResponse = {
  response: { buckets: ApiReasonBucket[] };
};

const buildBreakdownUrl = (from?: Date, to?: Date): string => {
  return (
    apiRoutes.FAILED_REASON_BREAKDOWN +
    '?' +
    qs.stringify({
      ...(from ? { from: utcToLocal(from).toISOString() } : {}),
      ...(to ? { to: utcToLocal(to).toISOString() } : {}),
    })
  );
};

export const useFailedReasonBreakdown = (params: {
  from?: Date;
  to?: Date;
}): { breakdown: ReasonBucket[]; isLoading: boolean } => {
  const currentUrl = buildBreakdownUrl(params.from, params.to);

  const windowMs =
    params.from && params.to
      ? params.to.getTime() - params.from.getTime()
      : 0;

  const priorTo = params.from
    ? new Date(params.from.getTime() - 1)
    : undefined;
  const priorFrom = priorTo
    ? new Date(priorTo.getTime() - windowMs)
    : undefined;

  const priorUrl = buildBreakdownUrl(priorFrom, priorTo);

  const { data, isLoading } = useQuery<BreakdownApiResponse>(currentUrl, {
    staleTime: 60_000,
  });

  const { data: priorData } = useQuery<BreakdownApiResponse>(priorUrl, {
    staleTime: 60_000,
    enabled: !!priorFrom && !!priorTo,
  });

  const breakdown: ReasonBucket[] = (
    data?.response?.buckets ?? []
  ).map((bucket) => {
    const prior =
      priorData?.response?.buckets?.find((b) => b.reason === bucket.reason)
        ?.count ?? 0;
    const priorTotal = priorData?.response?.buckets?.reduce(
      (s, b) => s + b.count,
      0
    ) ?? 0;

    let trend: 'up' | 'down' | 'stable' = 'stable';
    if (priorTotal > 0) {
      if (prior === 0 && bucket.count > 0) {
        trend = 'up';
      } else if (prior > 0) {
        const change = (bucket.count - prior) / prior;
        if (change > 0.1) trend = 'up';
        else if (change < -0.1) trend = 'down';
      }
    }

    return {
      reason: bucket.reason,
      count: bucket.count,
      pct: bucket.percentage,
      trend,
      priorCount: prior,
    };
  });

  return { breakdown, isLoading };
};

export const usePlaceholderProps = (): UsePlaceholderPropsReturnType => {
  const intl = useIntl();

  const title = intl.formatMessage({
    id: 'orders.noFailedOrders',
    defaultMessage: 'No Failed Orders',
  });

  const description = intl.formatMessage({
    id: 'orders.failedOrdersPlaceholderDescription',
    defaultMessage: 'Failed Orders Placeholder Description',
  });

  const Icon = FailedOrder;

  return {
    title,
    description,
    Icon,
  };
};

export const useExportOrders = (): {
  isLoading: boolean;
  handleDownloadOrdersExcelFile: (filters: OrdersGridFilters) => Promise<any>;
} => {
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const { addToast } = useToasts();
  const intl = useIntl();

  const handleDownloadOrdersExcelFile = async (filters: OrdersGridFilters) => {
    try {
      setIsLoading(true);

      const res = await axios
        .get(makeApiUrl(apiRoutes.ORDERS_EXPORT), {
          params: filters,
          paramsSerializer: (params) => qs.stringify(params),
          responseType: 'blob',
        })
        .finally(() => setIsLoading(false));

      if (res.status === 200 && res.data) {
        const file = new Blob([res.data]);

        downloadFile(file, `orders - ${formatDateDashes(new Date())}.xlsx`);
      } else {
        addToast(intl.formatMessage({ id: 'failed' }), {
          appearance: 'error',
        });
      }
    } catch (error) {
      addToast(intl.formatMessage({ id: 'failed' }), {
        appearance: 'error',
      });
    }
  };

  return {
    isLoading,
    handleDownloadOrdersExcelFile,
  };
};

export const useBulkCancelOrders = (): {
  isLoading: boolean;
  bulkCancel: (orderIds: string[], onDone: () => void) => Promise<void>;
} => {
  const [isLoading, setIsLoading] = useState(false);
  const { addToast } = useToasts();

  const bulkCancel = async (orderIds: string[], onDone: () => void) => {
    setIsLoading(true);
    let successCount = 0;
    let failCount = 0;

    await Promise.all(
      orderIds.map(async (orderId) => {
        try {
          const detailsRes = await axios.get(
            makeApiUrl(apiRoutes.ORDER_SUMMARY_ORDER_DETAILS(orderId))
          );
          const fulfillmentId = detailsRes.data?.response?.fulfillmentId;
          if (!fulfillmentId) {
            failCount++;
            return;
          }
          const cancelFn = makeMutationFn(apiRoutes.CANCEL_ORDER(orderId));
          await cancelFn({ data: { fulfillmentId } });
          successCount++;
        } catch {
          failCount++;
        }
      })
    );

    setIsLoading(false);

    if (successCount > 0) {
      addToast(`${successCount} order${successCount !== 1 ? 's' : ''} cancelled`, {
        appearance: 'success',
      });
    }
    if (failCount > 0) {
      addToast(`${failCount} order${failCount !== 1 ? 's' : ''} failed to cancel`, {
        appearance: 'error',
      });
    }

    onDone();
  };

  return { isLoading, bulkCancel };
};
